import React, { useEffect, useMemo, useState } from 'react';
import { SkeletonLines } from '@/components/Admin/UI/Skeleton';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import {
   getFirmwareCandidates,
   sendFirmwareToDevices,
   getCoinBoxCandidates,
   sendCoinBoxToDevices,
   getBundleSendCandidates,
   getConfigReleaseCandidates,
   sendConfigReleaseToRouters,
   sendBundleToRouters,
   type FirmwareCandidate,
} from '@/api/admin';
import { useUIStore } from '@/store/useUIStore';

/**
 * Send one firmware version to CHOSEN devices — a canary.
 *
 * WHY (Ray, 2026-08-11): activating armed every box whose version differed, all
 * or nothing, so putting an image on three boxes meant editing the production
 * database by hand. I did that three times in one day against live customer
 * devices. That is fine for an engineer and impossible for an operator, and it
 * is how canarying quietly stops being something the team does.
 *
 * Two things this screen must do that a plain checkbox list would not:
 *
 *   Show what each box is RUNNING. Choosing from names alone is how a hybrid
 *   image reaches a cloud box — a one-way migration onto a product with no cloud
 *   coin path to land on.
 *
 *   Name every refusal. Ticking five boxes and being told "3 sent" with no
 *   explanation is how someone decides the feature is broken.
 */
const SendFirmware: React.FC<{
   data: { id: string; version: string; target?: 'router' | 'nodemcu' | 'bundle' | 'mikrotik-config' };
}> = ({ data }) => {
   // ONE picker for both. The two endpoints answer different questions — a coin
   // box is reached through its router and can be too old to update at all —
   // but the screen asking is identical, and a second copy of this file would
   // drift on the first change. The NodeMCU response is adapted to this shape
   // in the api layer.
   const isCoinBox = data.target === 'nodemcu';
   const isBundle = data.target === 'bundle';
   // MikroTik router settings: applied right away by the server (no download).
   const isMikrotikConfig = data.target === 'mikrotik-config';
   const fetchCandidates = isMikrotikConfig
      ? getConfigReleaseCandidates
      : isBundle
      ? getBundleSendCandidates
      : isCoinBox
        ? getCoinBoxCandidates
        : getFirmwareCandidates;
   const sendToDevices = isMikrotikConfig
      ? sendConfigReleaseToRouters
      : isBundle
      ? sendBundleToRouters
      : isCoinBox
        ? sendCoinBoxToDevices
        : sendFirmwareToDevices;
   const { handleClose } = useUIStore();
   const [loading, setLoading] = useState(true);
   const [devices, setDevices] = useState<FirmwareCandidate[]>([]);
   const [variant, setVariant] = useState<'cloud' | 'hybrid'>('cloud');
   const [picked, setPicked] = useState<Set<string>>(new Set());
   const [q, setQ] = useState('');
   const [sending, setSending] = useState(false);

   useEffect(() => {
      let alive = true;
      fetchCandidates(data.id)
         .then((r) => {
            if (!alive) return;
            setDevices(r.devices);
            setVariant(r.variant);
         })
         .catch(() => toast.error('Could not load the device list'))
         .finally(() => alive && setLoading(false));
      return () => {
         alive = false;
      };
   }, [data.id]);

   const shown = useMemo(() => {
      const t = q.trim().toLowerCase();
      const match = (d: FirmwareCandidate) =>
         !t ||
         d.name.toLowerCase().includes(t) ||
         d.deviceId.toLowerCase().includes(t) ||
         // By owner too: a device called "STARLINK PISOWIFI 1" tells you nothing
         // about whose it is, and canarying is usually decided per operator.
         d.ownerEmail?.toLowerCase().includes(t) ||
         d.ownerName?.toLowerCase().includes(t);
      // Already-sent first, then the ones you can pick, then the rest. Reopening
      // this modal must SHOW what you already did — a checkbox list that forgets
      // is worse than none, because you cannot tell a box you skipped from one
      // you already sent to (Ray, 2026-08-11).
      const rank = (d: FirmwareCandidate) =>
         d.onThisVersion || d.armedForThis ? 0 : d.eligible ? 1 : 2;
      // The wrong PRODUCT is hidden, not greyed (Ray, 2026-08-11). A cloud image
      // can never go to a hybrid box, so in a list of 269 those rows are noise
      // you have to read past — same argument as excluding the wrong board.
      // The count is shown below instead, so a box you cannot find is explained
      // rather than simply absent.
      return devices
         .filter((d) => d.skipReason !== 'wrong-variant')
         .filter(match)
         .sort((a, b) => rank(a) - rank(b));
   }, [devices, q]);

   /**
    * Already sent to, or already running it — nothing left to do here.
    *
    * Declared ABOVE every use. It was below the useMemo that calls it, which
    * crashed the page the moment a box was ticked: the memo re-ran, reached a
    * const that had not been initialised yet, and threw. Nothing showed until
    * then because the check short-circuits before calling it while nothing is
    * ticked — so the bug waited for the first click.
    */
   const done = (d: FirmwareCandidate) => d.onThisVersion || d.armedForThis;

   const hiddenVariant = devices.filter(
      (d) => d.skipReason === 'wrong-variant'
   ).length;
   const onCount = devices.filter((d) => d.onThisVersion).length;
   const sentCount = devices.filter((d) => d.armedForThis).length;

   const eligiblePicked = useMemo(
      () => devices.filter((d) => picked.has(d.id) && d.eligible && !done(d)),
      [devices, picked]
   );

   const toggle = (d: FirmwareCandidate) => {
      if (!d.eligible || done(d)) return;
      setPicked((prev) => {
         const next = new Set(prev);
         next.has(d.id) ? next.delete(d.id) : next.add(d.id);
         return next;
      });
   };

   const send = async () => {
      setSending(true);
      try {
         const r = await sendToDevices(
            data.id,
            eligiblePicked.map((d) => d.id)
         );
         toast.success(r.summary);
         r.skipped.forEach((s) => toast.info(`${s.name || s.deviceId}: ${s.why}`));
         handleClose();
      } catch {
         toast.error('Could not send the update');
      } finally {
         setSending(false);
      }
   };

   const fromVersions = Array.from(
      new Set(eligiblePicked.map((d) => (d.runningVersion || 'unknown').split('-')[0]))
   );

   return (
      <Wrap>
         <Lead>
            Send <b>{data.version}</b> to chosen devices only. This does not
            change which version the rest of the fleet is on.
         </Lead>

         {(sentCount > 0 || onCount > 0) && (
            <Already>
               Already sent to <b>{sentCount + onCount}</b> device
               {sentCount + onCount === 1 ? '' : 's'}
               {onCount > 0 && ` — ${onCount} already running it`}
               {sentCount > 0 && `, ${sentCount} still to take it`}.
            </Already>
         )}

         <Search
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by device name, ID, or owner email…"
         />

         {loading ? (
            <SkeletonLines lines={3} />
         ) : (
            <List>
               {shown.map((d) => (
                  <Row
                     key={d.id}
                     /* Greyed = cannot be sent this image at all. A done device
                        is NOT that — it is finished, which is a good outcome and
                        should not read like a refusal. */
                     $disabled={!d.eligible && !done(d)}
                     onClick={() => toggle(d)}
                     title={d.skipText ?? d.skipReason ?? undefined}
                  >
                     {/* Already done = ticked and locked. Ray sent an image,
                         reopened this, and saw every box unticked — so the only
                         safe read was "nothing happened". A tick you cannot
                         clear says "this one is handled" without inviting you to
                         send it twice. */}
                     <input
                        type="checkbox"
                        checked={done(d) || picked.has(d.id)}
                        disabled={!d.eligible || done(d)}
                        readOnly
                     />
                     <RowMain>
                        <RowName>{d.name || d.deviceId}</RowName>
                        <RowSub>
                           {d.ownerEmail && <Owner>{d.ownerEmail}</Owner>}
                           {d.deviceId} · {d.variant} ·{' '}
                           {/* What it runs NOW — the field that stops a hybrid
                               image reaching a cloud box. */}
                           <b>{d.runningVersion?.split('-')[0] || 'never reported'}</b>
                        </RowSub>
                     </RowMain>
                     {d.onThisVersion ? (
                        <State $tone="ok">on this version</State>
                     ) : d.armedForThis ? (
                        <State $tone="warn">sent — waiting for the box</State>
                     ) : (
                        !d.eligible && <Why>{d.skipText ?? d.skipReason}</Why>
                     )}
                  </Row>
               ))}
               {!shown.length && <Muted>No devices match that search.</Muted>}
            </List>
         )}

         {hiddenVariant > 0 && (
            <Muted>
               {hiddenVariant} {variant === 'cloud' ? 'hybrid' : 'cloud'} device
               {hiddenVariant === 1 ? '' : 's'} not shown — this is a{' '}
               {variant} image.
            </Muted>
         )}

         {eligiblePicked.length > 0 && (
            <Summary>
               <b>
                  {eligiblePicked.length} device
                  {eligiblePicked.length === 1 ? '' : 's'} will update from{' '}
                  {fromVersions.length === 1
                     ? fromVersions[0]
                     : `${fromVersions.length} versions`}{' '}
                  to {data.version.split('-')[0]}.
               </b>
               <div>
                  {isMikrotikConfig
                     ? 'The router applies it on its own, about a minute (a few minutes with many sub-vendos); its row shows the new version when done. The router keeps running — only its web service restarts for a few seconds. Paid time is kept.'
                     : 'Each restarts once, about 4 minutes. Paid time is kept — customers keep their remaining minutes.'}
               </div>
            </Summary>
         )}

         <Actions>
            <Ghost onClick={handleClose}>Cancel</Ghost>
            <Primary
               disabled={sending || !eligiblePicked.length}
               onClick={send}
            >
               {sending
                  ? 'Sending…'
                  : !eligiblePicked.length
                    ? 'Choose devices first'
                    : `Send to ${eligiblePicked.length} device${eligiblePicked.length === 1 ? '' : 's'}`}
            </Primary>
         </Actions>
      </Wrap>
   );
};

export default SendFirmware;

const Wrap = styled.div`
   display: grid;
   gap: 12px;
`;
const Lead = styled.p`
   margin: 0;
   font-size: 14px;
   line-height: 1.5;
`;
const Search = styled.input`
   width: 100%;
   padding: 9px 12px;
   border: 1px solid rgba(0, 0, 0, 0.15);
   border-radius: 8px;
   font-size: 14px;
`;
const List = styled.div`
   max-height: 320px;
   overflow-y: auto;
   border: 1px solid rgba(0, 0, 0, 0.1);
   border-radius: 8px;
`;
const Row = styled.label<{ $disabled: boolean }>`
   display: flex;
   align-items: center;
   gap: 10px;
   padding: 9px 12px;
   border-bottom: 1px solid rgba(0, 0, 0, 0.06);
   cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
   opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
   &:last-child {
      border-bottom: none;
   }
`;
const RowMain = styled.div`
   flex: 1;
   min-width: 0;
`;
const RowName = styled.div`
   font-size: 13px;
   font-weight: 600;
`;
const RowSub = styled.div`
   font-size: 12px;
   opacity: 0.7;
`;
const State = styled.span<{ $tone: 'ok' | 'warn' }>`
   font-size: 11px;
   font-weight: 600;
   white-space: nowrap;
   color: ${({ $tone }) => ($tone === 'ok' ? '#16a34a' : '#d97706')};
`;
const Already = styled.p`
   margin: 0;
   padding: 8px 12px;
   border-radius: 8px;
   background: rgba(22, 163, 74, 0.08);
   border: 1px solid rgba(22, 163, 74, 0.25);
   font-size: 13px;
`;
const Owner = styled.span`
   display: block;
   opacity: 0.9;
`;
const Why = styled.span`
   font-size: 11px;
   opacity: 0.75;
   white-space: nowrap;
`;
const Summary = styled.div`
   padding: 10px 12px;
   border-radius: 8px;
   background: rgba(37, 99, 235, 0.08);
   border: 1px solid rgba(37, 99, 235, 0.25);
   font-size: 13px;
   line-height: 1.45;
   display: grid;
   gap: 4px;
`;
const Muted = styled.p`
   margin: 0;
   padding: 12px;
   font-size: 13px;
   opacity: 0.7;
`;
const Actions = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 8px;
`;
const Ghost = styled.button`
   padding: 9px 16px;
   border-radius: 8px;
   border: 1px solid rgba(0, 0, 0, 0.15);
   background: none;
   cursor: pointer;
`;
const Primary = styled.button`
   padding: 9px 16px;
   border-radius: 8px;
   border: none;
   background: #2563eb;
   color: #fff;
   font-weight: 600;
   cursor: pointer;
   &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
   }
`;
